import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { StudentJobService } from '../../../core/services/student-job-management/student-job.service';
import { DeepLinkUtil } from '../../../core/utils/deep-link.util';

@Component({
  selector: 'app-public-job-details',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './public-job-details.component.html',
  styleUrl: './public-job-details.component.scss'
})
export class PublicJobDetailsComponent implements OnInit, OnDestroy {
  job: any = null;
  isLoading = true;
  jobId: string | null = null;
  private subscription = new Subscription();
  isBrowser: boolean;
  showShareFallback: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private studentJobService: StudentJobService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit() {
    this.subscription.add(
      this.route.paramMap.subscribe(params => {
        this.jobId = params.get('id');
        if (this.jobId) {
          this.fetchJobDetails(this.jobId);
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  fetchJobDetails(id: string) {
    this.isLoading = true;
    this.subscription.add(
      this.studentJobService.getAllStudentJobDetails(id).subscribe({
        next: (res: any) => {
          if (res.Success && res.Data) {
            this.job = res.Data;
          }
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
        }
      })
    );
  }

  applyNow() {
    // Deep Linking: Intercept mobile clicks to open the native app directly to this specific job.
    // We pass the dynamic jobId so the app knows exactly which job to load.
    if (DeepLinkUtil.isAndroid()) {
      window.location.href = DeepLinkUtil.getAndroidIntentUrl(`student-dashboard/jobs/apply/${this.jobId}`);
      return;
    }
    // Navigate to login and specify the redirectUrl to go directly to the job application page
    this.router.navigate(['/login'], {
      queryParams: { redirectUrl: `/student-dashboard/jobs/apply/${this.jobId}` }
    });
  }

  saveJob() {
    if (this.isBrowser && this.jobId) {
      localStorage.setItem('pending_save_job_id', this.jobId);
    }
    this.router.navigate(['/login'], {
      queryParams: { redirectUrl: '/student-dashboard/jobs/saved' }
    });
  }

  goBack(event: Event) {
    event.preventDefault();
    this.router.navigate(['/']).then(() => {
      setTimeout(() => {
        const element = document.getElementById('jobs');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    });
  }

  async shareJob() {
    if (!this.isBrowser || !this.job) return;

    const shareData = {
      title: `${this.job.job_title} at ${this.job.company_name} - BeExpert`,
      text: `Check out this amazing opportunity: ${this.job.job_title} at ${this.job.company_name}!`,
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback for browsers that don't support Web Share API
        this.toggleShareFallback();
      }
    } catch (err) {
      // Gracefully fail share in production
    }
  }

  toggleShareFallback() {
    this.showShareFallback = !this.showShareFallback;
  }

  shareTo(platform: string) {
    if (!this.isBrowser) return;

    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(`Check out this amazing opportunity: ${this.job?.job_title} at ${this.job?.company_name}!`);

    let shareUrl = '';
    switch (platform) {
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${text}%20${url}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
        break;

    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
    this.showShareFallback = false;
  }

  async copyLink() {
    if (!this.isBrowser) return;
    try {
      await navigator.clipboard.writeText(window.location.href);
      // Optional: you can show a toast here, but we keep it simple or use visual feedback
      const copyBtn = document.getElementById('copy-btn');
      if (copyBtn) {
        const originalText = copyBtn.innerHTML;
        copyBtn.innerHTML = 'Copied!';
        setTimeout(() => copyBtn.innerHTML = originalText, 2000);
      }
    } catch (err) {
      // Gracefully fail copy
    }
  }

  getTimeAgo(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1d ago';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return `${Math.floor(diffDays / 30)}mo ago`;
  }

  scrollTo(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      // scroll to element with some offset for navbar
      const y = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }
}
